import { useEffect, useState } from 'react';
import { useI18nStore } from '@/store/i18nStore';
import api from '@/services/api';

interface ChartDataPoint {
  date: string;
  value: number;
}

interface ChartSeries {
  label: string;
  unit: string;
  period: string;
  data: ChartDataPoint[];
  current?: number;
}

interface DashboardCharts {
  pool_trend: ChartSeries;
  fill_rate: ChartSeries;
  payout_trend: ChartSeries;
  box_completions: ChartSeries;
  fund_balance: ChartSeries;
}

/**
 * MiniChart — Grafico SVG minimale, dark mode first.
 */
function MiniChart({ series, color = '#646cff' }: { series: ChartSeries; color?: string }) {
  const values = series.data.map((d) => d.value);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const width = 280;
  const height = 80;

  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * width;
    const y = height - ((v - min) / range) * (height - 10) - 5;
    return `${x},${y}`;
  });

  const areaPoints = [...points, `${width},${height}`, `0,${height}`];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${series.label.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints.join(' ')}
        fill={`url(#grad-${series.label.replace(/\s/g, '')})`}
      />
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ChartCard — Card per un singolo grafico.
 */
function ChartCard({
  series,
  color,
  icon,
}: {
  series: ChartSeries;
  color: string;
  icon: string;
}) {
  const latest = series.data[series.data.length - 1]?.value ?? 0;
  const prev = series.data[series.data.length - 2]?.value ?? 0;
  const change = prev !== 0 ? ((latest - prev) / prev) * 100 : 0;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          <h3 className="text-sm font-medium text-surface-600 dark:text-surface-400">{series.label}</h3>
        </div>
        <span className="text-xs text-surface-400 dark:text-surface-500">{series.period}</span>
      </div>

      <div className="flex items-end justify-between mb-3">
        <p className="text-2xl font-bold text-surface-900 dark:text-white">
          {series.current !== undefined
            ? `€${series.current.toLocaleString()}`
            : series.unit === '%'
              ? `${latest.toFixed(1)}%`
              : series.unit === 'EUR'
                ? `€${latest.toLocaleString()}`
                : latest.toLocaleString()
          }
        </p>
        {change !== 0 && (
          <span className={`text-xs font-medium ${change > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
            {change > 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>

      <MiniChart series={series} color={color} />
    </div>
  );
}

/**
 * AnalyticsCharts — Pagina grafici avanzati per admin dashboard.
 */
export default function AnalyticsCharts() {
  const t = useI18nStore((s) => s.t);
  const [charts, setCharts] = useState<DashboardCharts | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/charts?days=${days}`)
      .then((res) => setCharts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!charts) {
    return (
      <div className="text-center py-12 text-surface-400 dark:text-surface-500">
        {t('charts.noData')}
      </div>
    );
  }

  return (
    <div>
      {/* Period selector */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm text-surface-500 dark:text-surface-400">{t('charts.title')}:</span>
        {[
          { d: 7, label: t('charts.last7days') },
          { d: 30, label: t('charts.last30days') },
          { d: 90, label: t('charts.last90days') },
        ].map((opt) => (
          <button
            key={opt.d}
            onClick={() => setDays(opt.d)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              days === opt.d
                ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400'
                : 'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Charts grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ChartCard series={charts.pool_trend} color="#646cff" icon="💰" />
        <ChartCard series={charts.fill_rate} color="#22c55e" icon="📊" />
        <ChartCard series={charts.payout_trend} color="#f59e0b" icon="💸" />
        <ChartCard series={charts.box_completions} color="#06b6d4" icon="📦" />
        <ChartCard series={charts.fund_balance} color="#8b5cf6" icon="🏦" />
      </div>
    </div>
  );
}
